import { useReducer, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getOriginData } from "./EditTrailerRow";
import instance from "../../Interceptor/api_instance";
import { FormInput, FormSelect } from "../../Components/Inputs/FormInputs";
import {
  CARRIER_OPTIONS,
  COUNTRY_OPTIONS,
  VEHICLE_CONDITIONS,
} from "../../Constants/CheckInOut";
import TruckSearchSelect from "../../Components/Inputs/TruckSearch";
import handleTruckSelect from "../../Components/Inputs/TruckSearchHelper";
import { mexicanStates, usStates } from "../../Helper/CountryStates";
import { toDatetimeLocal } from "../../Helper/FormatDatetimes";
import { useFetch } from "../../Hooks/useFetch";
import { useApiMutation } from "../../Hooks/useApiMutation";
import { Spinner } from "../../Components/LoadingAnimations/LoadingAnimations";

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_CARRIER":
      return {
        ...state,
        carrierName: action.payload,
      };
  }
};

const initialState = {
  carrierName: "",
};

export const EditTruckRow = ({ close, row, onSuccess }) => {
  const { t } = useTranslation();

  // Fetch truck detail by ID
  const { data, isLoading, error } = useFetch(
    ({ signal }) =>
      instance
        .get(`assets/trucks/${row.truck.id}/`, { signal })
        .then((res) => res.data),
    [row.truck.id]
  );

  const {
    mutate: updateTruck,
    isLoading: updateLoading,
    error: updateError,
  } = useApiMutation(async (payload) => {
    const response = await instance.put("assets/edit-truck", payload);
    return response;
  });

  const [truckData, setTruckData] = useState({});

  const [formState, dispatchForm] = useReducer(reducer, initialState);

  // Populate form state once fetch completes
  useEffect(() => {
    if (data) {
      const [city, state, country] = getOriginData(data.origin);
      setTruckData({
        id: row.id,
        truckAsset: data.truck_asset ? data.truck_asset.id : null,
        carrierName: data.carrier_name,
        odometer: data.odometer || "",
        truckOption: data.truck_asset
          ? { label: data.truck_asset.name, value: data.truck_asset.id }
          : null,
        truckUnit: data.unit,
        truckNumber: data.truck_asset ? "" : data.truck_number,
        truckPlate: data.truck_plate,
        truckCondition: data.truck_condition,
        truckConditionNotes: data.truck_condition_notes,
        eta: data.estimated_completion
          ? toDatetimeLocal(data.estimated_completion)
          : null,
        city: city,
        state: state,
        country: country,
      });
      dispatchForm({
        type: "SET_CARRIER",
        payload: data.truck_asset ? data.carrier_name.toLowerCase() : "other",
      });
    }
  }, [data]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTruckData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const postEditChanges = async (e) => {
    e.preventDefault();
    const payload = {
      id: truckData.id,
      truck_asset: truckData.truckAsset,
      carrier_name: truckData.carrierName,
      odometer: truckData.odometer,
      truck_number: truckData.truckNumber,
      truck_plate: truckData.truckPlate,
      truck_condition: truckData.truckCondition,
      truck_condition_notes: truckData.truckConditionNotes,
      origin: `${truckData.city}, ${truckData.state}, ${truckData.country}`,
      eta: truckData.eta,
    };

    try {
      const response = await updateTruck(payload);
      if (response.status === 200) {
        onSuccess(response.data, "truck");
      }
    } catch (err) {
      console.log(err);
    }
  };

  if (error) return <p className="error">{error}</p>;

  return (
    <div className="page-mask page-mask-animation">
      <div className="edit-modal">
        {isLoading ? (
          <Spinner />
        ) : (
          <div>
            <h4 className="global-h2">{t("forms.type.edit-entry")}</h4>
            <p className="gray">{truckData.truckUnit}</p>
            <form onSubmit={(e) => postEditChanges(e)}>
              {/* Form group: Carrier */}
              <FormSelect
                options={CARRIER_OPTIONS}
                label={"carrierName"}
                value={formState.carrierName}
                onChange={(e) => {
                  dispatchForm({
                    type: "SET_CARRIER",
                    payload: e.target.value,
                  });
                  if (e.target.value !== "other") {
                    setTruckData((prev) => ({
                      ...prev,
                      carrierName: e.target.value,
                      truckAsset: null,
                      truckNumber: "",
                      truckPlate: "",
                      truckOption: null,
                    }));
                  } else {
                    setTruckData((prev) => ({
                      ...prev,
                      carrierName: "",
                      truckAsset: null,
                      truckNumber: "",
                      truckPlate: "",
                      truckOption: null,
                    }));
                  }
                }}
              />
              {formState.carrierName === "other" && (
                <FormInput
                  label={"carrierName"}
                  value={truckData.carrierName}
                  onChange={handleInputChange}
                />
              )}
              <div className="form-group">
                <label className="input-label">
                  {t("yard-tables.table-columns.truck")}
                </label>
                <div className="input-cont">
                  {formState.carrierName !== "other" ? (
                    <TruckSearchSelect
                      onSelect={(option) =>
                        handleTruckSelect({
                          selectedTruck: option,
                          setFormData: setTruckData,
                          dispatchForm,
                        })
                      }
                      carrier={truckData.carrierName}
                      selectedTruck={truckData.truckOption}
                    />
                  ) : (
                    <input
                      type="text"
                      className="text-input"
                      name="truckNumber"
                      value={truckData.truckNumber}
                      onChange={handleInputChange}
                      required
                    />
                  )}
                </div>
              </div>

              <FormInput
                label={"truckPlate"}
                value={truckData.truckPlate}
                onChange={handleInputChange}
              />

              <FormInput
                label={"odometer"}
                value={truckData.odometer}
                type="number"
                onChange={handleInputChange}
              />

              <FormSelect
                options={VEHICLE_CONDITIONS}
                label={"truckCondition"}
                value={truckData.truckCondition}
                onChange={handleInputChange}
              />

              {(truckData.truckCondition === "under-repair" ||
                truckData.truckCondition === "waiting-parts" ||
                truckData.truckCondition === "waiting-instructions") && (
                <FormInput
                  label={"eta"}
                  value={truckData.eta}
                  onChange={handleInputChange}
                  type="datetime-local"
                />
              )}

              <FormInput
                label={"truckConditionNotes"}
                onChange={handleInputChange}
                value={truckData.truckConditionNotes}
                required={
                  truckData.truckCondition?.toLowerCase() !== "good" &&
                  truckData.truckCondition?.toLowerCase() !== "okay" &&
                  truckData.truckCondition?.toLowerCase() !== "ready"
                }
              />

              <div className="form-group">
                <label className="input-label">
                  {t("forms.type.vehicle.input-headers.origin")}
                </label>
              </div>
              <FormSelect
                options={COUNTRY_OPTIONS}
                label={"country"}
                value={truckData.country}
                onChange={handleInputChange}
              />

              <div className="form-group">
                <label className="input-label required">
                  {t("forms.type.vehicle.labels.state")}
                </label>
                <div className="input-cont">
                  <select
                    id="state"
                    name="state"
                    data-testid="state"
                    value={truckData.state}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="" disabled>
                      {t("forms.type.vehicle.placeholders.destination.state")}
                    </option>
                    {truckData.country?.toLowerCase() === "usa"
                      ? usStates.map((state, index) => (
                          <option key={index} value={state}>
                            {state}
                          </option>
                        ))
                      : mexicanStates.map((state, index) => (
                          <option key={index} value={state}>
                            {state}
                          </option>
                        ))}
                  </select>
                </div>
              </div>

              <FormInput
                label={"city"}
                value={truckData.city}
                onChange={handleInputChange}
              />

              {updateError && <p className="error">{updateError}</p>}
              <div className="full-cont">
                {updateLoading ? (
                  <Spinner />
                ) : (
                  <>
                    <button type="submit" className="other-btn">
                      {t("buttons.update")}
                    </button>
                    <button type="button" onClick={close} className="close-btn">
                      {t("buttons.close")}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};
